import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import logoImg from '../../logo.png';
import { publicApi } from '../../services/api';
import { flattenSettings, getThemeColors, getSettingValue } from '../../utils/settings';
import { parseSectionContent } from '../../utils/sectionContent';

const STATIC_LINKS = [
  { path: '/', label: 'Beranda' },
  { path: '/about', label: 'Tentang Kami' },
  { path: '/products', label: 'Produk' },
  { path: '/order', label: 'Pesan' },
  { path: '/blog', label: 'Blog' },
  { path: '/contact', label: 'Kontak' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmsPages, setCmsPages] = useState([]);
  const [pagesOpen, setPagesOpen] = useState(false);
  const [mobileCmsOpen, setMobileCmsOpen] = useState(false);
  const [brand, setBrand] = useState({ name: 'Klinnovac', logo: logoImg, colors: { primary: '#081626', secondary: '#00CFFF' } });
  const dropdownRef = useRef(null);
  const location = useLocation();
  const isOrderPage = location.pathname === '/order' || location.pathname.startsWith('/order/');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setPagesOpen(false); setMobileCmsOpen(false); }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setPagesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      publicApi.getPages().catch(() => ({ data: { data: { data: [] } } })),
      publicApi.getSettings().catch(() => ({ data: { data: {} } })),
      publicApi.getPage('home').catch(() => ({ data: { data: { sections: [] } } })),
    ]).then(([pagesRes, settingsRes, homeRes]) => {
      if (cancelled) return;
      const pages = pagesRes.data?.data?.data || pagesRes.data?.data || [];
      const staticSlugs = STATIC_LINKS.map(l => l.path.replace('/', ''));
      const dynamicPages = pages
        .filter(p => p.status === 'published' && !staticSlugs.includes(p.slug) && p.slug !== 'home' && p.slug !== 'home-klinnovac')
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setCmsPages(dynamicPages);

      const flat = flattenSettings(settingsRes.data?.data || {});
      const sections = homeRes.data?.data?.sections || [];
      const brandingSection = sections.find(section => section.type === 'branding');
      const brandingContent = parseSectionContent(brandingSection?.content);
      setBrand({
        name: brandingContent.site_name || brandingContent.name || getSettingValue(flat, ['site_name', 'site_title'], 'Klinnovac'),
        logo: brandingContent.logo_url || brandingContent.image_url || flat.site_logo || logoImg,
        colors: getThemeColors(flat),
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isCmsPageActive = cmsPages.some(p => location.pathname === `/${p.slug}`);

  const linkClass = (active) =>
    `text-sm font-medium transition-colors hover:text-brand ${
      scrolled ? 'text-gray-700' : 'text-white'
    } ${active ? 'text-brand font-semibold' : ''}`;

  const mobileLinkClass = (active) =>
    `block py-2 px-3 rounded-lg text-sm font-medium ${
      active ? 'bg-brand/10 text-brand-dark' : 'text-gray-700 hover:bg-gray-50'
    }`;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {!isOrderPage ? (
          <Link to="/" className="flex items-center gap-2">
            <img src={brand.logo || logoImg} alt={brand.name} className="h-12 w-auto object-contain" />
            <span className={`text-xl font-bold transition-colors ${scrolled ? 'text-brand-dark' : 'text-white'}`} style={{ fontFamily: "'Cabin', sans-serif", color: scrolled ? brand.colors.primary : 'white' }}>{brand.name}</span>
          </Link>
        ) : (
          <div className="flex items-center gap-2">
            <span className={`text-xl font-bold transition-colors ${scrolled ? 'text-brand-dark' : 'text-white'}`} style={{ fontFamily: "'Cabin', sans-serif", color: scrolled ? brand.colors.primary : 'white' }}>Pesan Layanan</span>
          </div>
        )}

        <div className="hidden md:flex items-center gap-5">
          {STATIC_LINKS.map(link => (
            <Link key={link.path} to={link.path} className={linkClass(isActive(link.path))}>
              {link.label}
            </Link>
          ))}
          {cmsPages.length > 0 && (
            <div className="relative" ref={dropdownRef}>
              <button onClick={() => setPagesOpen(!pagesOpen)} className={`flex items-center gap-1 ${linkClass(isCmsPageActive)}`}>
                Lainnya
                <ChevronDown className={`w-4 h-4 transition-transform ${pagesOpen ? 'rotate-180' : ''}`} />
              </button>
              {pagesOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 rounded-xl shadow-lg border border-gray-100 py-2 z-50 bg-white">
                  {cmsPages.map(page => (
                    <Link key={page.id} to={`/${page.slug}`} onClick={() => setPagesOpen(false)}
                      className={`block px-4 py-2 text-sm transition-colors ${location.pathname === `/${page.slug}` ? 'bg-brand/10 text-brand-dark font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                      {page.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2">
          {mobileOpen ? <X className={`w-6 h-6 ${scrolled ? 'text-gray-800' : 'text-white'}`} /> : <Menu className={`w-6 h-6 ${scrolled ? 'text-gray-800' : 'text-white'}`} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="px-4 py-3 space-y-1">
            {STATIC_LINKS.map(link => (
              <Link key={link.path} to={link.path} className={mobileLinkClass(isActive(link.path))}>{link.label}</Link>
            ))}
            {cmsPages.length > 0 && (
              <div>
                <button onClick={() => setMobileCmsOpen(!mobileCmsOpen)}
                  className={`w-full flex items-center justify-between py-2 px-3 rounded-lg text-sm font-medium ${isCmsPageActive ? 'bg-brand/10 text-brand-dark' : 'text-gray-700 hover:bg-gray-50'}`}>
                  Halaman Lainnya
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileCmsOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileCmsOpen && (
                  <div className="ml-3 mt-1 space-y-1 border-l-2 border-gray-100 pl-3">
                    {cmsPages.map(page => (
                      <Link key={page.id} to={`/${page.slug}`}
                        className={`block py-2 px-3 rounded-lg text-sm ${location.pathname === `/${page.slug}` ? 'bg-brand/10 text-brand-dark font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>
                        {page.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}